"use client";

import * as React from "react";
import { useTranslation } from "react-i18next";
import Countdown, { CountdownRendererFn } from "react-countdown";
import { useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { useRouter } from "next/navigation";
import { ChannelIcon } from "@audacia-hq/shared/components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { useLivechat } from "@audacia-hq/shared/contexts";

import { Consultation, TrialConsultation } from "../../models/consultation";
import { useExpert } from "../../context/ExpertContext";
import { Expert } from "../../models/expert";

type ChannelType = "PHONE" | "VIDEO" | "LIVECHAT" | "WRITTEN";

interface Props {
  consultation?: Partial<Consultation>
  trialConsultation?: Partial<TrialConsultation>
}

const OngoingConsultationBanner: React.FC<Props> = ({ consultation, trialConsultation }) => {
  const { t } = useTranslation("common");
  const { getExperts } = useExpert();
  const { unreadMessages } = useLivechat();
  const [expert, setExpert] = useState<Expert>();
  const [countdownFrom, setCountdownFrom] = useState<Dayjs>();
  const router = useRouter();

  React.useEffect(() => {
    if (!consultation && !trialConsultation) {
      setExpert(undefined);
      return;
    }
    setCountdownFrom(consultation?.startDate ? dayjs(consultation.startDate).add(consultation?.maxDuration || 0, "seconds") : undefined);
    getExperts([consultation?.expertUid || trialConsultation?.expertUid]).then((e) => setExpert(e[0] || undefined));
  }, [consultation, trialConsultation]);

  const renderCountDown: CountdownRendererFn = ({
    hours, minutes, seconds,
  }) => (
    <span className="text-orange-400 ml-1 font-semibold">
      {`${(hours).toLocaleString("fr-CH", { minimumIntegerDigits: 2 })}${t("common:hourLetter")} 
      ${minutes.toLocaleString("fr-CH", { minimumIntegerDigits: 2 })}${t("common:minuteLetter")} 
      ${seconds.toLocaleString("fr-CH", { minimumIntegerDigits: 2 })}${t("common:secondLetter")}`}
    </span>
  );

  const renderCountUp: CountdownRendererFn = ({
    hours, minutes, seconds, total,
  }) => (
    <span className="text-green-500 ml-1 font-semibold">
      {`${hours.toLocaleString("fr-CH", { minimumIntegerDigits: 2 })}${t("common:hourLetter")} 
        ${minutes.toLocaleString("fr-CH", { minimumIntegerDigits: 2 })}${t("common:minuteLetter")} 
        ${seconds.toLocaleString("fr-CH", { minimumIntegerDigits: 2 })}${t("common:secondLetter")}`}
    </span>
  );
  const handleBannerClick = () => {
    if (consultation?.channel === "LIVECHAT") {
      router.push(`/livechat/${consultation.channelRefUid}`);
    }
    if (consultation?.channel === "PHONE") {
      router.push(`/phone/${consultation.consultationUid}`);
    }
    if (consultation?.channel === "VIDEO") {
      // TODO IMPLEMENT FOR VIDEO (change url if needed)
      router.push(`/video/${consultation.channelRefUid}`);
    }
    if (trialConsultation?.channel === "LIVECHAT") {
      router.push(`/livechat/${trialConsultation.channelRefUid}`);
    }
    if (trialConsultation?.channel === "PHONE") {
      router.push(`/phone/${trialConsultation.trialConsultationUid}`);
    }
    if (trialConsultation?.channel === "VIDEO") {
      // TODO IMPLEMENT FOR VIDEO (change url if needed)
      router.push(`/video/${trialConsultation.channelRefUid}`);
    }
  };

  if (!consultation && (!trialConsultation || trialConsultation.status === "PENDING_FEEDBACK")) return <></>;
  return (
    <div className="bg-black">
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
      <div
        className=" text-white py-3 cursor-pointer wrapper items-center"
        onClick={handleBannerClick}
      >
        <div className="flex flex-row flex-nowrap mx-5 justify-between flex-1">
          <div className="whitespace-nowrap min-w-0 shrink overflow-hidden text-ellipsis flex space-x-2 hover:text-gray-300 hover items-center">
            <ChannelIcon channel={(consultation?.channel || trialConsultation?.channel as ChannelType)} />
            {" "}
            {expert && (<span className="hidden sm:inline">{t("consultationBanner.consultationWithExpert", { expert: expert.pseudo })}</span>)}
            {expert && (<span className="sm:hidden">{ expert.pseudo }</span>)}
            {consultation?.channel === "LIVECHAT" && (unreadMessages || 0) > 0 && (
              <div className="bg-red-600 rounded-full pt-[1px] text-xs h-4 w-4  text-white flex justify-center">
                {unreadMessages > 9 ? (
                  <>
                    9
                    <span style={{ fontSize: "70%" }}>+</span>
                  </>
                ) : unreadMessages}
              </div>
            )}
            <div className="flex-none"><FontAwesomeIcon icon={faChevronRight} /></div>

          </div>
          {consultation && countdownFrom?.isAfter(dayjs()) && (
            <div className="whitespace-nowrap flex items-center">
              <span className="hidden mobile:inline">{t("consultationBanner.maxDuration")}</span>
              {" "}
              <Countdown date={countdownFrom.toDate()} renderer={renderCountDown} />
            </div>
          )}
          {trialConsultation && trialConsultation.startDate && (
            <div className="whitespace-nowrap flex items-center">
              <span className="hidden mobile:inline">{t("consultationBanner.elapsedDuration")}</span>
              {" "}
              <Countdown overtime date={dayjs(trialConsultation.startDate).toDate()} renderer={renderCountUp} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OngoingConsultationBanner;
